import { Injectable } from '@angular/core';
import { CurrencyPipe } from '@angular/common';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';

import { StringPipe } from '@shared/pipes';
import { IProperty } from '@shared/models/properties';

const { rebuiltOrgId } = environment;

@Injectable({
  providedIn: 'root',
})
export class MapService {
  constructor(private router: Router) {}

  createMap(
    divId: string,
    center?: google.maps.LatLngLiteral
  ): google.maps.Map | undefined {
    const mapDiv = document.getElementById(divId) as HTMLElement;
    if (!mapDiv) return;
    if (!google.maps) return;

    const map = new google.maps.Map(mapDiv, {
      mapId: 'f81574a6005415bd',
      center: center || { lat: 36.1758796, lng: -86.7969797 },
      zoom: 8,
      minZoom: 3,
      restriction: {
        latLngBounds: { north: 85, south: -85, west: -180, east: 180 },
        strictBounds: false,
      },
      fullscreenControl: true,
    });
    return map;
  }

  removeAdvancedMarker(marker: google.maps.marker.AdvancedMarkerElement) {
    marker.map = null;
  }

  createMarker(mapRef: google.maps.Map, property: IProperty) {
    if (!property.latitude || !property.longitude) return;
    const mapItemWrapper = document.createElement('div');
    mapItemWrapper.classList.add('map-item-wrapper');
    mapItemWrapper.setAttribute('data-property', property.id);
    mapItemWrapper.appendChild(this.createPriceTag(property));
    mapItemWrapper.appendChild(this.createItemPreview(property));

    const marker = new google.maps.marker.AdvancedMarkerElement({
      map: mapRef,
      position: {
        lat: parseFloat(property.latitude),
        lng: parseFloat(property.longitude),
      },
      content: mapItemWrapper,
    });

    google.maps.event.addListener(marker, 'click', () => {
      const propertyId = property.id;
      const itemPreview = document.querySelector(
        `.item-preview[data-property="${propertyId}"]`
      );

      const isAlreadyOpen = itemPreview?.classList.contains('flex');

      // Open property page in a new tab on second click
      if (isAlreadyOpen) {
        const orgSlug =
          property.organization.id !== rebuiltOrgId
            ? '/o/' + property.organization.shortName
            : '';

        try {
          const newTab = window.open(
            `${orgSlug}/properties/${propertyId}`,
            '_blank'
          );
          if (
            !newTab ||
            newTab.closed ||
            typeof newTab.closed === 'undefined'
          ) {
            throw new Error('Popup blocked');
          }
        } catch (error) {
          this.router.navigate(['/properties', propertyId]);
        }
      } else {
        // Close other previews and open this one
        document.querySelectorAll('.item-preview.flex').forEach((el) => {
          el.classList.remove('flex');
          el.classList.add('hidden');
          el.parentElement?.parentElement?.parentElement?.classList.remove(
            'z-10'
          );
        });

        itemPreview?.classList.remove('hidden');
        itemPreview?.classList.add('flex');
        itemPreview?.parentElement?.parentElement?.parentElement?.classList.add(
          'relative'
        );
        itemPreview?.parentElement?.parentElement?.parentElement?.classList.add(
          'z-10'
        );
      }
    });

    // If marker is near the edge of the map
    // we should add a class to the map item wrapper
    // to adjust the position of the preview
    mapRef.addListener('center_changed', () => {
      const mapBounds = mapRef.getBounds();
      const markerPosition = marker.position;
      if (mapBounds && markerPosition) {
        const mapWidth =
          mapBounds.getNorthEast().lng() - mapBounds.getSouthWest().lng();
        const mapHeight =
          mapBounds.getNorthEast().lat() - mapBounds.getSouthWest().lat();
        const mapCenter = mapBounds.getCenter();
        const markerOffset = {
          x: (markerPosition.lng as number) - mapCenter.lng(),
          y: (markerPosition.lat as number) - mapCenter.lat(),
        };
        const markerOffsetPercentage = {
          x: markerOffset.x / mapWidth,
          y: markerOffset.y / mapHeight,
        };
        const mapItemWrapper = marker.content as HTMLElement;
        if (markerOffsetPercentage.x < -0.1) {
          mapItemWrapper.classList.remove('center');
          mapItemWrapper.classList.add('left');
        } else if (markerOffsetPercentage.x > 0.1) {
          mapItemWrapper.classList.remove('center');
          mapItemWrapper.classList.add('right');
        } else {
          mapItemWrapper.classList.add('center');
          mapItemWrapper.classList.remove('left');
          mapItemWrapper.classList.remove('right');
        }
        if (markerOffsetPercentage.y < -0.3) {
          mapItemWrapper.classList.add('top');
        } else if (markerOffsetPercentage.y > 0.3) {
          mapItemWrapper.classList.add('bottom');
        } else {
          mapItemWrapper.classList.remove('top');
          mapItemWrapper.classList.remove('bottom');
        }
      }
    });
    return marker;
  }

  createPriceTag(property: IProperty) {
    function convertToTwoDecimalPlaces(num: number) {
      const numString = num.toString();
      const decimalIndex = numString.indexOf('.');
      if (decimalIndex === -1) return num;
      const decimalPlaces = numString.length - decimalIndex - 1;
      if (decimalPlaces <= 2) return num;
      return parseFloat(numString.slice(0, decimalIndex + 3));
    }

    const price = parseFloat(property.price) / 1000;
    const priceTag = document.createElement('div');
    priceTag.classList.add(
      'price-tag',
      'status-' + property.status.toLowerCase()
    );
    if (!property.isVerified) {
      priceTag.classList.add('unverified');
    }
    priceTag.textContent =
      price < 1000
        ? `$${Math.round(price)}K`
        : `$${convertToTwoDecimalPlaces(price / 1000)}M`;
    priceTag.setAttribute('data-property', property.id);
    return priceTag;
  }

  createItemPreview(property: IProperty) {
    let image = '/assets/images/property-no-image.svg';
    if (property.images?.length) {
      image = property.images[0].thumbnail.url;
    }

    const itemPreview = document.createElement('div');
    itemPreview.classList.add('item-preview');
    itemPreview.classList.add('hidden');
    itemPreview.setAttribute('data-property', property.id);
    const itemPreviewImage = document.createElement('img');
    itemPreviewImage.src = image;
    itemPreview.appendChild(itemPreviewImage);
    const itemTopbar = document.createElement('div');
    itemTopbar.classList.add('item-topbar');
    itemPreview.appendChild(itemTopbar);
    const itemType = document.createElement('div');
    itemType.classList.add('property-type');
    itemType.textContent = new StringPipe().transform(
      property.subType,
      'capitalizeFirstLetter'
    );
    itemTopbar.appendChild(itemType);
    const itemStatus = document.createElement('div');
    itemStatus.classList.add('item-status');
    itemStatus.textContent = !property.isVerified
      ? 'UNVERIFIED'
      : property.status === 'OPEN' || property.status === 'UNVERIFIED'
      ? 'ACTIVE'
      : property.status;
    itemTopbar.appendChild(itemStatus);
    const itemPrice = document.createElement('div');
    itemPrice.classList.add('item-price');
    itemPrice.textContent = new CurrencyPipe('en-US').transform(
      property.price,
      'USD',
      'symbol',
      '1.0-2'
    );
    itemPreview.appendChild(itemPrice);
    const itemBottom = document.createElement('div');
    itemBottom.classList.add('item-bottom');
    itemPreview.appendChild(itemBottom);
    const itemBeds = document.createElement('div');
    itemBeds.classList.add('item-beds');
    itemBeds.textContent = `${Number(property.bedrooms) || '-'} Beds`;
    itemBottom.appendChild(itemBeds);
    const itemBaths = document.createElement('div');
    itemBaths.classList.add('item-baths');
    itemBaths.textContent = `${Number(property.bathrooms) || '-'} Baths`;
    itemBottom.appendChild(itemBaths);
    if (property.squareFoot) {
      const itemSqft = document.createElement('div');
      itemSqft.classList.add('item-sqft');
      itemSqft.textContent = `${Number(property.squareFoot) || '-'} sqft`;
      itemBottom.appendChild(itemSqft);
    }

    return itemPreview;
  }
}
